import type { Dapp } from '@src/dto';
import { AuthStateType, type AuthState } from '../../../hooks/useAuth';
import {
  isReleaseDebugLoggingEnabled,
  releaseDebugLog,
} from '../../../utils/releaseDebugLogger';
import { buildReleaseNftMetadataPreview } from './buildReleaseNftMetadataPreview';

interface LogNewVersionDebugPreviewParams {
  authState: AuthState;
  dapp: Dapp;
}

function getAuthenticatedUser(authState: AuthState) {
  return authState.type === AuthStateType.Authenticated ? authState.user : null;
}

function logHubSpotPayloadPreview(authState: AuthState, dapp: Dapp) {
  const authenticatedUser = getAuthenticatedUser(authState);
  const isInitialSubmission = dapp.releaseCount === 0;
  const formId = isInitialSubmission
    ? 'a4c87dc2-7e9f-40d7-9b7a-6cf93c0fc21f'
    : '08f59c7f-e9f3-4981-a3d5-43f079e6c8f5';

  releaseDebugLog(
    '\n=== HubSpot Payload Preview (will be sent after NFT minting) ===',
  );
  releaseDebugLog(
    'Submission Type:',
    isInitialSubmission ? 'Initial Release' : 'Update Release',
  );
  releaseDebugLog('Form ID:', formId);
  releaseDebugLog(
    'Form URL:',
    isInitialSubmission
      ? 'https://api.hsforms.com/submissions/v3/integration/submit/26958416/a4c87dc2-7e9f-40d7-9b7a-6cf93c0fc21f'
      : 'https://api.hsforms.com/submissions/v3/integration/submit/26958416/08f59c7f-e9f3-4981-a3d5-43f079e6c8f5',
  );

  releaseDebugLog('\nFields that will be sent to HubSpot:');
  releaseDebugLog('Contact Fields:');
  releaseDebugLog(
    '  - email:',
    authenticatedUser?.email ||
      '[user email - will be populated at submission]',
  );
  releaseDebugLog('  - company:', dapp.publisherName);
  releaseDebugLog('  - website:', dapp.publisherWebsite);

  releaseDebugLog('\nTicket Fields:');
  releaseDebugLog('  - content: [full ticket content - see below]');
  releaseDebugLog(
    '  - attestation_payload: [empty - will be generated at submission time]',
  );
  releaseDebugLog(
    '  - dapp_collection_account_address:',
    dapp.nftMintAddress || '[App NFT not minted yet]',
  );
  releaseDebugLog(
    '  - dapp_release_account_address: [will be generated during NFT minting]',
  );
  releaseDebugLog(
    '  - google_play_store_package_name:',
    dapp.packageName || '[will be extracted from APK]',
  );

  if (!isInitialSubmission) {
    releaseDebugLog('  - critical_update: false (default, can be changed)');
    releaseDebugLog('  - testing_instructions: [optional field]');
  }

  releaseDebugLog('\nTicket Content Preview:');
  if (isInitialSubmission) {
    releaseDebugLog(`New Release Submission: ${dapp.dappName} v[version]
            
App Details:
- Name: ${dapp.dappName}
- Package: [will be extracted from APK]
- Version: [will be extracted from APK]
- Subtitle: ${dapp.subtitle || 'N/A'}
- Description: ${dapp.description}
- Support Email: ${dapp.supportEmail}
- App Website: ${dapp.appWebsite || 'Not provided'}

Release Information:
- Version Name: [from APK]
- Version Code: [from APK]
- Min SDK: [from APK]
- Target SDK: [from APK]
- Certificate Fingerprint: [from APK]
- Permissions: [from APK]

Publisher Information:
- Name: ${dapp.publisherName || '[publisher name]'}
- Type: [publisher type - Individual or Organization]
- User: ${authenticatedUser?.fullName || '[user full name]'} (${authenticatedUser?.email || '[user email]'})

NFT Information:
- App NFT: ${dapp.nftMintAddress || '[not minted yet]'}
- Release NFT: [will be generated]

Release Metadata:
- Short Description: [if provided]
- Long Description: [if provided]
- Localized Name: [if provided, otherwise ${dapp.dappName}]

Legal Documents:
- License URL: ${dapp.licenseUrl || 'Not provided'}
- Copyright URL: ${dapp.copyrightUrl || 'Not provided'}
- Privacy Policy URL: ${dapp.privacyPolicyUrl || 'Not provided'}

Submission Type: Initial Release`);
  } else {
    releaseDebugLog(`Release Update Submission: ${dapp.dappName} v[version]
            
App Details:
- Name: ${dapp.dappName}
- Package: [from APK]
- Current Version: [from APK]

What's New:
[will be entered in form]

Release Information:
- Version Name: [from APK]
- Version Code: [from APK]
- Min SDK: [from APK]
- Target SDK: [from APK]
- Certificate Fingerprint: [from APK]

Publisher Information:
- Name: ${dapp.publisherName || '[publisher name]'}
- Type: [publisher type - Individual or Organization]
- User: ${authenticatedUser?.fullName || '[user full name]'} (${authenticatedUser?.email || '[user email]'})

NFT Information:
- App NFT: ${dapp.nftMintAddress || '[not found]'}
- Release NFT: [will be generated]

Critical Update: No (default)

Submission Type: Update Release`);
  }

  releaseDebugLog('\n=== End HubSpot Payload Preview ===\n');
}

export function logNewVersionDebugPreview({
  authState,
  dapp,
}: LogNewVersionDebugPreviewParams) {
  if (!isReleaseDebugLoggingEnabled()) {
    return;
  }

  releaseDebugLog('Dapp data loaded:', dapp);
  releaseDebugLog('releaseCount:', dapp.releaseCount);
  releaseDebugLog('isFirstRelease will be:', dapp.releaseCount === 0);

  logHubSpotPayloadPreview(authState, dapp);

  releaseDebugLog(
    '\n=== Release NFT Metadata Preview (will be created on submission) ===',
  );
  releaseDebugLog(
    'Metadata Structure:',
    JSON.stringify(
      buildReleaseNftMetadataPreview({
        authState,
        dapp,
      }),
      null,
      2,
    ),
  );
  releaseDebugLog(
    '\nNOTE: Values in [BRACKETS] will be populated during the submission process',
  );
  releaseDebugLog('=== End Release NFT Metadata Preview ===\n');
}
